import { useRef } from 'react';
import { Volume2 } from 'lucide-react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';

const EXERCISE_TYPES_WITH_SOUND = [
  'singleChoiceExercise',
  'multipleChoiceExercise',
  'anagramExercise',
  'boggleExercise',
  'wordPoolExercise',
  'oddOneOutExercise',
  'sortingExercise',
  'unjumbleExercise',
  'rankExercise',
  'dragLabelImageExercise',
  'dragLabelTextExercise',
  'dragLabelBlankExercise',
  'matchLinesExercise',
  'labelImageExercise',
  'typeAnswerExercise',
  'trueFalseExercise',
  'abcdExercise',
  'chooseWordExercise',
  'fixMistakeExercise',
  'fillGapsExercise',
  'tableFillExercise',
];

export const supportsSoundBadge = (type) => EXERCISE_TYPES_WITH_SOUND.includes(type);

// Top-right music icon overlay shown on interactive tasks that have a `soundUrl`
// attached. In `interactive` mode, clicking restarts the audio from the start —
// the clip has no scrubber and cannot be paused (mirrors the audio element's
// player controls but without them).
const ExerciseSoundBadge = ({ soundUrl, interactive = false }) => {
  const { t } = useTranslation();
  const audioRef = useRef(null);

  if (!soundUrl) return null;
  const resolved = resolveMediaUrl(soundUrl);

  const handleClick = (e) => {
    if (!interactive) return;
    e.stopPropagation();
    e.preventDefault();
    if (!audioRef.current) {
      audioRef.current = new Audio(resolved);
    }
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => { /* user-gesture rejected — ignore */ });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseDown={(e) => interactive && e.stopPropagation()}
      title={interactive ? t('games.common.playSound') : t('games.common.soundAdded')}
      className={`absolute top-1 right-1 z-10 flex items-center justify-center w-7 h-7 rounded-full shadow-md bg-white border border-gray-200 text-brand-primary ${
        interactive ? 'cursor-pointer hover:bg-brand-sage/30 active:scale-95 transition' : 'cursor-default pointer-events-none'
      }`}
    >
      <Volume2 size={14} />
    </button>
  );
};

export default ExerciseSoundBadge;
